import { useState, useEffect } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { Menu, MenuItem, Checkbox, ListItemText } from '@mui/material';
import Pill from '@ingka/pill';
import Badge from '@ingka/badge';
import chevronDown from '@ingka/ssr-icon/paths/chevron-down';

type ConfidentialityLevel =
  | 'Confidential'
  | 'Internal'
  | 'Strictly confidential';

const CONFIDENTIALITY_OPTIONS: ConfidentialityLevel[] = [
  'Confidential',
  'Internal',
  'Strictly confidential',
];

export const ConfidentialityFilter = () => {
  const apiRef = useGridApiContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [selectedLevels, setSelectedLevels] = useState<ConfidentialityLevel[]>([
    'Internal',
  ]);

  const [selectAllMode, setSelectAllMode] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isAllSelected =
    selectedLevels.length === CONFIDENTIALITY_OPTIONS.length;

  const handleToggleLevel = (level: ConfidentialityLevel) => {
    if (selectAllMode) return;

    setSelectedLevels((prev) => {
      const exists = prev.includes(level);
      const next = exists ? prev.filter((l) => l !== level) : [...prev, level];
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectAllMode) {
      setSelectAllMode(false);
      setSelectedLevels(['Internal']);
      return;
    }
    setSelectAllMode(true);
    setSelectedLevels(CONFIDENTIALITY_OPTIONS);
  };

  const isFiltered = selectedLevels.length > 0;
  const activeFilterCount = isFiltered ? selectedLevels.length : 0;

  useEffect(() => {
    if (!isFiltered) {
      apiRef.current.deleteFilterItem({
        id: 'confidential-filter',
        operator: 'isAnyOf',
        field: 'confidential',
      });
    } else {
      apiRef.current.upsertFilterItem({
        field: 'confidential',
        operator: 'isAnyOf',
        value: selectedLevels,
        id: 'confidential-filter',
      });
    }
  }, [selectAllMode, selectedLevels, apiRef]);

  return (
    <>
      <Pill
        onClick={handleClick}
        size="small"
        trailingIcon={chevronDown}
        selected={isFiltered}
        label={
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            Confidentiality
            {activeFilterCount > 0 && (
              <Badge
                colour="black"
                label={String(activeFilterCount)}
                size="small"
              />
            )}
          </span>
        }
      />

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        disableScrollLock
        disablePortal
        PaperProps={{ style: { position: 'absolute' } }}
      >
        {/* Select All */}
        <MenuItem
          onClick={() => {
            handleToggleSelectAll();
            handleClose();
          }}
          disabled={!selectAllMode && isAllSelected}
        >
          <Checkbox checked={selectAllMode || isAllSelected} />
          <ListItemText primary="Select All" />
        </MenuItem>

        {/* Internal */}
        <MenuItem
          onClick={() => {
            handleToggleLevel('Internal');
            handleClose();
          }}
          disabled={selectAllMode}
        >
          <Checkbox
            checked={
              selectedLevels.includes('Internal') || (selectAllMode && true)
            }
          />
          <ListItemText primary="Internal" />
        </MenuItem>

        {/* Confidential */}
        <MenuItem
          onClick={() => {
            handleToggleLevel('Confidential');
            handleClose();
          }}
          disabled={selectAllMode}
        >
          <Checkbox
            checked={
              selectedLevels.includes('Confidential') || (selectAllMode && true)
            }
          />
          <ListItemText primary="Confidential" />
        </MenuItem>

        {/* Strictly confidential */}
        <MenuItem
          onClick={() => {
            handleToggleLevel('Strictly confidential');
            handleClose();
          }}
          disabled={selectAllMode}
        >
          <Checkbox
            checked={
              selectedLevels.includes('Strictly confidential') ||
              (selectAllMode && true)
            }
          />
          <ListItemText primary="Strictly confidential" />
        </MenuItem>
      </Menu>
    </>
  );
};
